import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Quote, ArrowRight } from 'lucide-react';
import { MarketingNav } from '@/components/landing/MarketingNav';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { LeadCaptureForm } from '@/components/landing/LeadCaptureForm';
import { SEOHead } from '@/components/SEOHead';
import { SchemaMarkup, generateBreadcrumbSchema } from '@/components/SchemaMarkup';
import { Breadcrumbs } from '@/components/Breadcrumbs';

export default function DemoPage() {
  const { t, i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');

  const bullets = [
    t('landing.demo.bullet1'),
    t('landing.demo.bullet2'),
    t('landing.demo.bullet3'),
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title={isPt ? "Agende uma Demo — Veja o RevOps AI com Seus Dados | RevOps AI" : "Book a Demo — See RevOps AI with Your Data | RevOps AI"}
        description={isPt ? "Agende uma demonstração personalizada do RevOps AI. Veja como transformar os dados do seu CRM em receita previsível em menos de 30 minutos." : "Book a personalized demo of RevOps AI. See how to transform your CRM data into predictable revenue in under 30 minutes."}
        path="/demo"
      />
      <SchemaMarkup schema={[generateBreadcrumbSchema([
        { name: 'Home', url: 'https://revops.digitais.ai/' },
        { name: isPt ? 'Demo' : 'Demo', url: 'https://revops.digitais.ai/demo' },
      ])]} />
      <MarketingNav forceSolid />

      <main className="flex-1">
        <section className="py-16 md:py-24" style={{ background: 'linear-gradient(180deg, #F4F5FF 0%, #FFFFFF 100%)' }}>
          <div className="container max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Agendar Demo' : 'Book a Demo' }]} />
          </div>
          <div className="container">
            <div className="grid gap-12 md:grid-cols-5 items-start max-w-6xl mx-auto">
              {/* Left side (60%) */}
              <div className="md:col-span-3">
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A1F3C] mb-8">
                  {t('landing.demo.headline')}
                </h1>

                <ul className="space-y-4 mb-10">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-[#00C896] mt-0.5 flex-shrink-0" />
                      <span className="text-[#1A1F3C]/70">{b}</span>
                    </li>
                  ))}
                </ul>

                {/* Testimonial */}
                <div className="rounded-xl border border-[#1A1F3C]/8 bg-[#F4F5FF]/50 p-6">
                  <Quote className="h-6 w-6 text-[#4F6CFF]/30 mb-3" />
                  <p className="text-[#1A1F3C]/70 text-sm italic mb-4">
                    "{t('landing.demo.testimonialQuote')}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1F3C]">{t('landing.demo.testimonialName')}</p>
                    <p className="text-xs text-[#1A1F3C]/50">{t('landing.demo.testimonialRole')}</p>
                  </div>
                </div>
              </div>

              {/* Right side (40%) */}
              <div className="md:col-span-2">
                <LeadCaptureForm />

                <div className="mt-6 text-center">
                  <p className="text-sm text-[#1A1F3C]/50 mb-1">{t('landing.demo.preferSelfServe')}</p>
                  <Link to="/signup" className="text-sm font-medium text-[#4F6CFF] hover:underline inline-flex items-center gap-1">
                    {t('landing.demo.startTrial')}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
